import { motion } from "framer-motion";
import { ArrowRight, Phone } from "lucide-react";
import { useLang, ASSETS } from "../i18n";

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay: d, ease: [0.22, 1, 0.36, 1] },
});

export const Hero = () => {
  const { t } = useLang();
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="top" className="relative min-h-screen flex items-center overflow-hidden grain">
      {/* backdrop */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_30%,rgba(122,16,16,0.35),transparent_55%)]" />
      <div className="absolute inset-0 radial-glow opacity-70" />

      {/* Mercedes */}
      <motion.img
        src={ASSETS.mercedes}
        alt="Mercedes-AMG GT"
        initial={{ opacity: 0, x: 80, scale: 1.05 }}
        animate={{ opacity: 1, x: 0, scale: 1 }}
        transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        className="pointer-events-none select-none absolute right-[-6%] bottom-0 w-[62%] max-w-[900px] hidden md:block drop-shadow-[0_30px_60px_rgba(0,0,0,0.8)]"
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 w-full pt-28 pb-16">
        <div className="max-w-2xl">
          <motion.div {...fade(0)} className="flex items-center gap-3 mb-7">
            <span className="h-px w-10 bg-[var(--c-gold)]" />
            <span className="overline">{t.hero.tag}</span>
          </motion.div>

          <motion.h1 {...fade(0.1)} className="font-display font-semibold leading-[0.95] text-6xl sm:text-7xl lg:text-8xl">
            <span className="block text-white">{t.hero.titleA}</span>
            <span className="block gold-text italic">{t.hero.titleB}</span>
          </motion.h1>

          <motion.p {...fade(0.25)} className="mt-7 text-base sm:text-lg text-neutral-300 leading-relaxed max-w-xl">
            {t.hero.sub}
          </motion.p>

          <motion.div {...fade(0.4)} className="mt-10 flex flex-wrap items-center gap-4">
            <button
              data-testid="hero-cta-pricing"
              onClick={() => go("pricing")}
              className="group inline-flex items-center gap-2 bg-[var(--c-gold)] text-black font-semibold px-7 py-3.5 rounded-full hover:bg-[var(--c-gold-soft)] transition-colors"
            >
              {t.hero.ctaPrimary}
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              data-testid="hero-cta-contact"
              onClick={() => go("contact")}
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full gold-border text-[var(--c-gold)] hover:bg-white/5 transition-colors"
            >
              <Phone size={17} />
              {t.hero.ctaSecondary}
            </button>
          </motion.div>

          <motion.div {...fade(0.55)} className="mt-14 flex flex-wrap gap-x-10 gap-y-4">
            {[t.hero.stat1, t.hero.stat2, t.hero.stat3].map((s, i) => (
              <div key={i} className="flex items-center gap-2.5">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--c-yellow)]" />
                <span className="font-mono text-xs uppercase tracking-widest text-neutral-400">{s}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      <div className="absolute bottom-6 inset-x-0 flex justify-center z-10">
        <div className="shimmer-line h-px w-40" />
      </div>
    </section>
  );
};
